import { el, clear } from '../core/dom.js';
import { navigate } from '../router.js';
import { track as gaTrack } from '../analytics/ga.js';
import { uuid } from '../core/ids.js';
import { nowIso, localDate, tzOffsetMinutes, minutesSinceLocalMidnight } from '../core/time.js';
import {
  consumptionMethods, habits, plans, planStates, moneyModels, purchaseTemplates,
  settings as settingsStore, metaGet, metaSet, logEvent,
} from '../storage/db.js';
import { refresh } from '../core/state.js';
import { safetyFor } from '../copy/safety.js';
import {
  CATEGORY_TO_DOMAIN, NICOTINE_DEFAULT_METHODS, buildMethodFromSeed, defaultMethodForLegacyHabit,
} from '../domain/default-methods.js';
import type {
  BehaviorDomain, HabitCategory, HabitRecord, IntervalConfig, IntervalState,
  Pace, PlanRecord, PlanStateRecord, PlanType, PurchaseTemplateRecord, QuotaConfig, QuotaState,
  SavingsMode, SlipStyle, Tone, UserSettings, WindowConfig, WindowState,
} from '../core/types.js';

interface Draft {
  category?: HabitCategory;
  privateAlias?: string;
  unitSingular?: string;
  unitPlural?: string;
  patternType?: 'often' | 'context' | 'total' | 'unsure';
  planType?: PlanType;
  // interval
  intervalBaselineMin?: number; intervalStartMin?: number;
  // window
  windowFirstUseMinutes?: number; windowShiftStep?: number; windowName?: string;
  // quota
  quotaBaseline?: number; quotaStart?: number; quotaPeriod?: 'daily' | 'weekly' | 'monthly';
  goal?: string[];
  pace?: Pace;
  slipStyle?: SlipStyle;
  triggers?: string[];
  rescueTypes?: string[];
  rescueDurationSec?: number;
  unitsTouched?: boolean;
  /** Selected nicotine method kinds when domain is nicotine. */
  nicotineMethodKinds?: string[];
  moneyEnabled?: boolean;
  moneyEstimationType?: 'per_use' | 'per_pack' | 'weekly_spend' | 'monthly_spend';
  moneyCurrency?: string;
  moneyCostPerUse?: number;
  moneyPurchaseCost?: number;
  moneyUnitsPerPurchase?: number;
  moneyEstimatedSpend?: number;
  moneyPeriod?: 'day' | 'week' | 'month';
  motivation?: string;
  tone?: Tone;
}

let draft: Draft = {};

const HABIT_OPTIONS: { value: HabitCategory; label: string; defaultUnitS?: string; defaultUnitP?: string }[] = [
  { value: 'smoking', label: 'Nicotine', defaultUnitS: 'nicotine session', defaultUnitP: 'nicotine sessions' },
  { value: 'alcohol', label: 'Alcohol', defaultUnitS: 'drink', defaultUnitP: 'drinks' },
  { value: 'cannabis', label: 'Cannabis', defaultUnitS: 'session', defaultUnitP: 'sessions' },
  { value: 'caffeine', label: 'Caffeine', defaultUnitS: 'cup', defaultUnitP: 'cups' },
  { value: 'social_media', label: 'Social media', defaultUnitS: 'check-in', defaultUnitP: 'check-ins' },
  { value: 'gaming', label: 'Gaming', defaultUnitS: 'session', defaultUnitP: 'sessions' },
  { value: 'gambling', label: 'Gambling', defaultUnitS: 'session', defaultUnitP: 'sessions' },
  { value: 'porn', label: 'Porn', defaultUnitS: 'session', defaultUnitP: 'sessions' },
  { value: 'shopping', label: 'Shopping', defaultUnitS: 'purchase', defaultUnitP: 'purchases' },
  { value: 'snacking', label: 'Snacking', defaultUnitS: 'snack', defaultUnitP: 'snacks' },
  { value: 'other', label: 'Other / private label', defaultUnitS: 'use', defaultUnitP: 'uses' },
];

type Step = (host: HTMLElement, ctx: WizardCtx) => void;

interface WizardCtx { next: () => void; back: () => void; finish: () => void; rerender: () => void; index: number; total: number }

export function renderOnboarding(parent: HTMLElement) {
  // Fresh draft every time the wizard is opened (also clears state from a prior plan creation).
  draft = {};
  const wrap = el('div', { class: 'wizard' });
  const track = el('div', { class: 'progress-track' });
  const fill = el('div', { class: 'progress-fill', attrs: { style: 'width:0%' } });
  track.appendChild(fill);
  const stage = el('div');
  wrap.appendChild(track);
  wrap.appendChild(stage);
  parent.appendChild(wrap);

  const steps: Step[] = [
    stepHabit, stepNicotineMethods, stepUnit, stepPattern, stepBaseline, stepGoal, stepPace, stepSlip,
    stepTriggers, stepRescue, stepMoney, stepMotivation, stepTone, stepLocalNote, stepReview,
  ];
  let i = 0;
  let lastIndex = -1;
  const TRANSITION_CLASSES = ['step-in', 'step-in-forward', 'step-in-backward', 'step-out-forward', 'step-out-backward'];

  const mount = (direction: 'forward' | 'backward' | 'rerender' | 'initial') => {
    clear(stage);
    fill.setAttribute('style', `width:${Math.round(((i + 1) / steps.length) * 100)}%`);
    const ctx: WizardCtx = {
      next: () => { i = Math.min(steps.length - 1, i + 1); renderStep('forward'); },
      back: () => { i = Math.max(0, i - 1); renderStep('backward'); },
      finish: async () => {
        await commit();
        await refresh();
        navigate('/today');
      },
      rerender: () => renderStep('rerender'),
      index: i, total: steps.length,
    };
    const step = steps[i];
    if (!step) return;
    gaTrack('onboarding_step', { index: i, name: step.name || `step_${i}` });
    step(stage, ctx);
    stage.classList.remove(...TRANSITION_CLASSES);
    void stage.offsetWidth;
    if (direction === 'forward') stage.classList.add('step-in-forward');
    else if (direction === 'backward') stage.classList.add('step-in-backward');
    else if (direction === 'initial') stage.classList.add('step-in');
    if (i !== lastIndex) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      lastIndex = i;
    }
  };

  let transitioning = false;
  const OUT_DURATION_MS = 130;
  const renderStep = (direction: 'forward' | 'backward' | 'rerender' | 'initial' = 'initial') => {
    if (transitioning) return;
    if (direction === 'initial' || direction === 'rerender' || !stage.hasChildNodes()) {
      mount(direction);
      return;
    }
    transitioning = true;
    stage.classList.remove(...TRANSITION_CLASSES);
    void stage.offsetWidth;
    const outClass = direction === 'forward' ? 'step-out-forward' : 'step-out-backward';
    stage.classList.add(outClass);
    window.setTimeout(() => {
      transitioning = false;
      mount(direction);
    }, OUT_DURATION_MS);
  };
  renderStep('initial');
}

function actions(ctx: WizardCtx, opts: { primary: string; primaryDisabled?: boolean; onPrimary?: () => void; finish?: boolean } = { primary: 'Continue' }): HTMLElement {
  const back = ctx.index > 0
    ? el('button', { class: 'btn ghost', onclick: () => ctx.back() }, 'Back')
    : null;
  const primary = el('button', {
    class: 'btn primary',
    disabled: !!opts.primaryDisabled,
    onclick: () => { opts.onPrimary?.(); if (opts.finish) ctx.finish(); else ctx.next(); },
  }, opts.primary);
  const row = el('div', { class: 'wizard-actions' });
  if (back) row.appendChild(back);
  row.appendChild(primary);
  return row;
}

function choiceList<T extends string | number>(
  ctx: WizardCtx,
  current: T | undefined,
  options: Array<{ value: T; label: string; sub?: string }>,
  onPick: (v: T) => void,
): HTMLElement {
  const list = el('div', { class: 'choice-list' });
  for (const o of options) {
    const c = el('label', { class: `choice ${current === o.value ? 'selected' : ''}` },
      el('input', { type: 'radio', name: 'choice', checked: current === o.value, onchange: () => { onPick(o.value); ctx.rerender(); } }),
      el('div', {},
        el('div', {}, o.label),
        o.sub ? el('div', { class: 'text-muted', attrs: { style: 'font-size:0.8rem;' } }, o.sub) : null
      )
    );
    list.appendChild(c);
  }
  return list;
}

function multiChoice(ctx: WizardCtx, current: string[] | undefined, options: Array<{ value: string; label: string }>, onChange: (v: string[]) => void): HTMLElement {
  const set = new Set(current ?? []);
  const list = el('div', { class: 'choice-list' });
  for (const o of options) {
    const c = el('label', { class: `choice ${set.has(o.value) ? 'selected' : ''}` },
      el('input', {
        type: 'checkbox', checked: set.has(o.value),
        onchange: (e: Event) => {
          const t = e.target as HTMLInputElement;
          if (t.checked) set.add(o.value); else set.delete(o.value);
          onChange([...set]);
          c.classList.toggle('selected', t.checked);
          ctx.rerender();
        },
      }),
      el('div', {}, o.label)
    );
    list.appendChild(c);
  }
  return list;
}

const stepHabit: Step = (host, ctx) => {
  host.appendChild(el('h1', {}, 'What do you want to reduce?'));
  host.appendChild(el('p', { class: 'lead text-muted' }, 'Pick the closest fit. You can change the label later.'));
  host.appendChild(choiceList(ctx, draft.category, HABIT_OPTIONS.map((h) => ({ value: h.value, label: h.label })), (v) => {
    draft.category = v;
    const def = HABIT_OPTIONS.find((h) => h.value === v);
    // Update unit defaults whenever the category changes, unless the user has
    // typed their own unit on the next step.
    if (def && !draft.unitsTouched) {
      if (def.defaultUnitS !== undefined) draft.unitSingular = def.defaultUnitS;
      if (def.defaultUnitP !== undefined) draft.unitPlural = def.defaultUnitP;
    }
  }));
  if (draft.category) {
    const note = safetyFor(draft.category);
    if (note.short) {
      host.appendChild(el('div', {
        class: 'card',
        attrs: { style: `border-color: ${note.level === 'serious' ? 'var(--danger)' : 'var(--warn)'};` },
      },
        el('strong', {}, 'Heads up'),
        el('p', {}, note.short),
        ...(note.resources?.map((r) => el('div', { class: 'muted' }, r)) ?? []),
      ));
    }
  }
  host.appendChild(actions(ctx, { primary: 'Continue', primaryDisabled: !draft.category }));
};

const stepNicotineMethods: Step = (host, ctx) => {
  const cat = draft.category;
  const domain: BehaviorDomain | undefined = cat ? CATEGORY_TO_DOMAIN[cat] : undefined;
  if (domain !== 'nicotine') {
    // No methods to pick — keep the wizard linear.
    host.appendChild(actions(ctx, { primary: 'Continue' }));
    return;
  }
  if (draft.nicotineMethodKinds === undefined) {
    draft.nicotineMethodKinds = [cat === 'vaping' ? 'vape' : 'cigarette'];
  }
  host.appendChild(el('h1', {}, 'How do you usually get nicotine?'));
  host.appendChild(el('p', { class: 'lead text-muted' }, 'Pick all that apply. The plan will track them together.'));
  host.appendChild(multiChoice(ctx, draft.nicotineMethodKinds, [
    { value: 'cigarette', label: 'Cigarettes' },
    { value: 'vape',      label: 'Vapes' },
    { value: 'pouch',     label: 'Pouches' },
    { value: 'dip',       label: 'Dip' },
  ], (v) => draft.nicotineMethodKinds = v));
  host.appendChild(actions(ctx, {
    primary: 'Continue',
    primaryDisabled: (draft.nicotineMethodKinds?.length ?? 0) === 0,
  }));
};

const stepUnit: Step = (host, ctx) => {
  host.appendChild(el('h1', {}, 'What counts as one use?'));
  host.appendChild(el('p', { class: 'lead text-muted' }, 'Easy to log beats perfectly precise.'));
  const updateContinue = (): void => {
    const ok = !!(draft.unitSingular?.trim() && draft.unitPlural?.trim());
    const btn = host.querySelector('.wizard-actions .btn.primary') as HTMLButtonElement | null;
    if (btn) btn.disabled = !ok;
  };
  const sing = el('input', {
    value: draft.unitSingular ?? '', placeholder: 'e.g., cigarette',
    oninput: (e: Event) => {
      draft.unitSingular = (e.target as HTMLInputElement).value;
      draft.unitsTouched = true;
      updateContinue();
    },
  });
  const plur = el('input', {
    value: draft.unitPlural ?? '', placeholder: 'e.g., cigarettes',
    oninput: (e: Event) => {
      draft.unitPlural = (e.target as HTMLInputElement).value;
      draft.unitsTouched = true;
      updateContinue();
    },
  });
  const alias = el('input', {
    value: draft.privateAlias ?? '', placeholder: 'e.g., Plan A (optional)',
    oninput: (e: Event) => { draft.privateAlias = (e.target as HTMLInputElement).value; },
  });
  host.appendChild(el('div', { class: 'field' }, el('label', {}, 'Unit (singular)'), sing));
  host.appendChild(el('div', { class: 'field' }, el('label', {}, 'Unit (plural)'), plur));
  host.appendChild(el('div', { class: 'field' }, el('label', {}, 'Private label (optional)'), alias));
  host.appendChild(actions(ctx, { primary: 'Continue', primaryDisabled: !(draft.unitSingular?.trim() && draft.unitPlural?.trim()) }));
};

const stepPattern: Step = (host, ctx) => {
  host.appendChild(el('h1', {}, 'Which sounds most like this habit?'));
  host.appendChild(choiceList(ctx, draft.patternType, [
    { value: 'often', label: 'Many times throughout the day', sub: 'e.g., smoking, vaping, doomscrolling' },
    { value: 'context', label: 'At certain times or situations', sub: 'e.g., after work, before bed' },
    { value: 'total', label: 'I want to reduce the total amount', sub: 'e.g., drinks per week' },
    { value: 'unsure', label: 'Not sure', sub: 'We will help you choose' },
  ], (v) => {
    draft.patternType = v;
    draft.planType = v === 'often' ? 'interval' : v === 'context' ? 'window' : v === 'total' ? 'quota' : 'interval';
  }));
  host.appendChild(actions(ctx, { primary: 'Continue', primaryDisabled: !draft.patternType }));
};

const stepBaseline: Step = (host, ctx) => {
  const pt = draft.planType ?? 'interval';
  host.appendChild(el('h1', {}, 'Baseline'));
  let disabled = false;
  if (pt === 'interval') {
    host.appendChild(el('p', { class: 'lead text-muted' }, 'About how often does it happen now? We use this to set a starting gap.'));
    host.appendChild(choiceList(ctx, draft.intervalBaselineMin, [
      { value: 15, label: 'Every ~15 minutes' },
      { value: 30, label: 'Every ~30 minutes' },
      { value: 45, label: 'Every ~45 minutes' },
      { value: 60, label: 'Every ~1 hour' },
      { value: 120, label: 'Every ~2 hours' },
    ], (v) => {
      draft.intervalBaselineMin = v;
      draft.intervalStartMin = Math.max(5, Math.round(v * 1.1));
    }));
    disabled = draft.intervalBaselineMin === undefined;
  } else if (pt === 'window') {
    host.appendChild(el('p', { class: 'lead text-muted' }, 'When do you want the first use to happen, soonest?'));
    if (draft.windowFirstUseMinutes === undefined) {
      draft.windowFirstUseMinutes = minutesSinceLocalMidnight(new Date()) + 30;
    }
    draft.windowShiftStep = draft.windowShiftStep ?? 15;
    draft.windowName = draft.windowName ?? 'Daily';
    const input = el('input', {
      type: 'time',
      value: minutesToTime(draft.windowFirstUseMinutes),
      onchange: (e: Event) => { draft.windowFirstUseMinutes = timeToMinutes((e.target as HTMLInputElement).value); },
    });
    host.appendChild(el('div', { class: 'field' }, el('label', {}, 'Earliest first-use target'), input));
  } else {
    host.appendChild(el('p', { class: 'lead text-muted' }, 'How many uses per period?'));
    host.appendChild(choiceList(ctx, draft.quotaPeriod, [
      { value: 'daily', label: 'Daily cap' }, { value: 'weekly', label: 'Weekly cap' }, { value: 'monthly', label: 'Monthly cap' },
    ], (v) => draft.quotaPeriod = v));
    if (draft.quotaBaseline === undefined) draft.quotaBaseline = 14;
    if (draft.quotaStart === undefined) draft.quotaStart = 10;
    const baseline = el('input', {
      type: 'number', min: '1', value: String(draft.quotaBaseline),
      onchange: (e: Event) => { draft.quotaBaseline = Number((e.target as HTMLInputElement).value); },
    });
    const start = el('input', {
      type: 'number', min: '0', value: String(draft.quotaStart),
      onchange: (e: Event) => { draft.quotaStart = Number((e.target as HTMLInputElement).value); },
    });
    host.appendChild(el('div', { class: 'field' }, el('label', {}, 'Typical use this period (baseline)'), baseline));
    host.appendChild(el('div', { class: 'field' }, el('label', {}, 'Starting cap'), start));
    disabled = draft.quotaPeriod === undefined;
  }
  host.appendChild(actions(ctx, { primary: 'Continue', primaryDisabled: disabled }));
};

const stepGoal: Step = (host, ctx) => {
  host.appendChild(el('h1', {}, 'What are you hoping for right now?'));
  host.appendChild(multiChoice(ctx, draft.goal, [
    { value: 'slow_down', label: 'Slow down' },
    { value: 'spend_less', label: 'Spend less' },
    { value: 'control', label: 'Feel more in control' },
    { value: 'cut_a_lot', label: 'Cut back a lot' },
    { value: 'quit_eventually', label: 'Quit eventually' },
    { value: 'fewer_episodes', label: 'Fewer bad episodes' },
    { value: 'awareness', label: 'Build awareness first' },
  ], (v) => draft.goal = v));
  host.appendChild(actions(ctx, { primary: 'Continue' }));
};

const stepPace: Step = (host, ctx) => {
  host.appendChild(el('h1', {}, 'What pace feels sustainable?'));
  host.appendChild(choiceList(ctx, draft.pace, [
    { value: 'gentle', label: 'Gentle', sub: 'Smaller changes, more forgiveness.' },
    { value: 'steady', label: 'Steady', sub: 'Balanced. Recommended.' },
    { value: 'ambitious', label: 'Ambitious', sub: 'Faster stretching, firmer accountability.' },
  ], (v) => draft.pace = v));
  host.appendChild(actions(ctx, { primary: 'Continue', primaryDisabled: !draft.pace }));
};

const stepSlip: Step = (host, ctx) => {
  host.appendChild(el('h1', {}, 'When you slip, what helps you come back?'));
  host.appendChild(el('p', { class: 'text-muted' }, 'Your recovery style — Wean adapts its tone, not its support.'));
  host.appendChild(choiceList(ctx, draft.slipStyle, [
    { value: 'gentle', label: 'Gentle', sub: 'Soft language, low pressure.' },
    { value: 'balanced', label: 'Reflective', sub: 'Short pause to learn from it.' },
    { value: 'direct', label: 'Structured', sub: 'Clear next steps to recover.' },
  ], (v) => draft.slipStyle = v));
  host.appendChild(actions(ctx, { primary: 'Continue', primaryDisabled: !draft.slipStyle }));
};

const stepTriggers: Step = (host, ctx) => {
  host.appendChild(el('h1', {}, 'What usually pushes you toward it?'));
  host.appendChild(multiChoice(ctx, draft.triggers, [
    'stress', 'boredom', 'anxiety', 'loneliness', 'habit', 'after_work',
    'after_meals', 'before_bed', 'waking_up', 'driving', 'social', 'celebration',
    'anger', 'sadness', 'discomfort',
  ].map((v) => ({ value: v, label: v.replace(/_/g, ' ') })), (v) => draft.triggers = v));
  host.appendChild(actions(ctx, { primary: 'Continue' }));
};

const stepRescue: Step = (host, ctx) => {
  host.appendChild(el('h1', {}, 'What might help you wait?'));
  host.appendChild(multiChoice(ctx, draft.rescueTypes, [
    { value: 'minigame', label: 'Mini games' },
    { value: 'breathing', label: 'Breathing' },
    { value: 'movement', label: 'Movement' },
    { value: 'reflection', label: 'Quick journal' },
  ], (v) => draft.rescueTypes = v));
  host.appendChild(el('h2', { class: 'title-l', attrs: { style: 'margin-top:1rem;' } }, 'Default rescue length'));
  host.appendChild(choiceList(ctx, draft.rescueDurationSec, [
    { value: 30, label: '30 seconds' }, { value: 60, label: '1 minute' }, { value: 90, label: '90 seconds' }, { value: 180, label: '3 minutes' }, { value: 300, label: '5 minutes' },
  ], (v) => draft.rescueDurationSec = v));
  host.appendChild(actions(ctx, { primary: 'Continue' }));
};

const stepMoney: Step = (host, ctx) => {
  host.appendChild(el('h1', {}, 'Track money?'));
  host.appendChild(el('p', { class: 'lead text-muted' }, 'Optional. Pick whatever feels easiest to estimate.'));

  host.appendChild(choiceList(ctx, draft.moneyEnabled === undefined ? undefined : (draft.moneyEnabled ? 'yes' : 'no'),
    [{ value: 'yes', label: 'Yes, estimate savings' }, { value: 'no', label: 'Not now' }],
    (v) => { draft.moneyEnabled = v === 'yes'; if (v === 'yes' && !draft.moneyEstimationType) draft.moneyEstimationType = 'per_use'; if (v === 'yes' && !draft.moneyCurrency) draft.moneyCurrency = 'USD'; }));

  if (draft.moneyEnabled) {
    host.appendChild(el('div', { class: 'field' },
      el('label', {}, 'How do you usually pay?'),
      el('div', { class: 'choice-list' },
        ...([
          { value: 'per_use',       label: 'Per use',         sub: 'e.g., $0.50 per cigarette' },
          { value: 'per_pack',      label: 'Per pack/bundle', sub: 'e.g., $20 vape lasts ~1 week, 24-pack of beer is $35' },
          { value: 'weekly_spend',  label: 'Per week',        sub: 'rough weekly spend' },
          { value: 'monthly_spend', label: 'Per month',       sub: 'rough monthly spend' },
        ] as Array<{ value: 'per_use' | 'per_pack' | 'weekly_spend' | 'monthly_spend'; label: string; sub?: string }>).map((o) => {
          const c = el('label', { class: `choice ${draft.moneyEstimationType === o.value ? 'selected' : ''}` },
            el('input', {
              type: 'radio', name: 'moneyType', checked: draft.moneyEstimationType === o.value,
              onchange: () => { draft.moneyEstimationType = o.value; ctx.rerender(); },
            }),
            el('div', {}, el('div', {}, o.label), o.sub ? el('div', { class: 'text-muted', attrs: { style: 'font-size:0.8rem;' } }, o.sub) : null),
          );
          return c;
        }),
      ),
    ));

    host.appendChild(currencyField());
    for (const f of detailFields()) host.appendChild(f);
    host.appendChild(previewCard());
  }
  host.appendChild(actions(ctx, { primary: 'Continue' }));

  function currencyField(): HTMLElement {
    return el('div', { class: 'field' },
      el('label', {}, 'Currency'),
      el('select', {
        onchange: (e: Event) => { draft.moneyCurrency = (e.target as HTMLSelectElement).value; },
      },
        ...['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'INR', 'BRL', 'MXN'].map((c) => {
          const op = el('option', { value: c }, c);
          if ((draft.moneyCurrency ?? 'USD') === c) op.selected = true;
          return op;
        }),
      ),
    );
  }

  function detailFields(): HTMLElement[] {
    const out: HTMLElement[] = [];
    const t = draft.moneyEstimationType ?? 'per_use';
    const cur = currencySymbol(draft.moneyCurrency);
    if (t === 'per_use') {
      out.push(el('div', { class: 'field' },
        el('label', {}, `Cost per use (${cur})`),
        numField(draft.moneyCostPerUse, (n) => draft.moneyCostPerUse = n, '0.50'),
      ));
    } else if (t === 'per_pack') {
      out.push(el('div', { class: 'field' },
        el('label', {}, `Pack/bundle cost (${cur})`),
        numField(draft.moneyPurchaseCost, (n) => draft.moneyPurchaseCost = n, '20'),
      ));
      out.push(el('div', { class: 'field' },
        el('label', {}, `Uses per pack`),
        numField(draft.moneyUnitsPerPurchase, (n) => draft.moneyUnitsPerPurchase = n, '20'),
      ));
    } else if (t === 'weekly_spend') {
      draft.moneyPeriod = 'week';
      out.push(el('div', { class: 'field' },
        el('label', {}, `Weekly spend (${cur})`),
        numField(draft.moneyEstimatedSpend, (n) => draft.moneyEstimatedSpend = n, '40'),
      ));
    } else if (t === 'monthly_spend') {
      draft.moneyPeriod = 'month';
      out.push(el('div', { class: 'field' },
        el('label', {}, `Monthly spend (${cur})`),
        numField(draft.moneyEstimatedSpend, (n) => draft.moneyEstimatedSpend = n, '160'),
      ));
    }
    return out;
  }

  function numField(value: number | undefined, setter: (n: number) => void, placeholder: string): HTMLElement {
    return el('input', {
      type: 'number', step: '0.01', min: '0',
      value: value === undefined ? '' : String(value),
      placeholder,
      oninput: (e: Event) => { const v = Number((e.target as HTMLInputElement).value); setter(isNaN(v) ? 0 : v); },
      onchange: () => ctx.rerender(),
    });
  }

  function previewCard(): HTMLElement {
    const cpu = derivedCostPerUse();
    const cur = currencySymbol(draft.moneyCurrency);
    const hint = cpu === null
      ? 'Fill the fields above to see an estimate.'
      : `≈ ${cur}${cpu.toFixed(2)} per use · ${cur}${(cpu * 7).toFixed(2)} per week at one use a day · labelled as estimate.`;
    return el('div', { class: 'card', attrs: { style: 'margin-top:0.75rem;' } },
      el('h2', {}, 'Estimate'),
      el('p', { class: 'muted', attrs: { style: 'margin: 0;' } }, hint),
    );
  }
};

function currencySymbol(code: string | undefined): string {
  switch (code) {
    case 'EUR': return '€';
    case 'GBP': return '£';
    case 'JPY': return '¥';
    case 'INR': return '₹';
    case 'BRL': return 'R$';
    case 'MXN': return 'MX$';
    case 'CAD': return 'C$';
    case 'AUD': return 'A$';
    default: return '$';
  }
}

function derivedCostPerUse(): number | null {
  const t = draft.moneyEstimationType ?? 'per_use';
  if (t === 'per_use') return draft.moneyCostPerUse && draft.moneyCostPerUse > 0 ? draft.moneyCostPerUse : null;
  if (t === 'per_pack') {
    if (!draft.moneyPurchaseCost || !draft.moneyUnitsPerPurchase) return null;
    if (draft.moneyUnitsPerPurchase <= 0) return null;
    return draft.moneyPurchaseCost / draft.moneyUnitsPerPurchase;
  }
  // For period-based spend, we estimate per-use only when we know the baseline rate.
  const usesPerWeek = derivedBaselineUsesPerWeek();
  if (!usesPerWeek) return null;
  if (t === 'weekly_spend' && draft.moneyEstimatedSpend) return draft.moneyEstimatedSpend / usesPerWeek;
  if (t === 'monthly_spend' && draft.moneyEstimatedSpend) return draft.moneyEstimatedSpend / (usesPerWeek * 4.33);
  return null;
}

function derivedBaselineUsesPerWeek(): number | null {
  const pt = draft.planType;
  if (pt === 'interval' && draft.intervalBaselineMin) {
    // assume 16 active hours/day
    const usesPerDay = (16 * 60) / draft.intervalBaselineMin;
    return usesPerDay * 7;
  }
  if (pt === 'quota') {
    if (!draft.quotaBaseline) return null;
    const period = draft.quotaPeriod ?? 'weekly';
    if (period === 'daily') return draft.quotaBaseline * 7;
    if (period === 'weekly') return draft.quotaBaseline;
    return draft.quotaBaseline / 4.33;
  }
  return null;
}

const stepMotivation: Step = (host, ctx) => {
  host.appendChild(el('h1', {}, 'What do you want back from this habit?'));
  host.appendChild(el('p', { class: 'lead text-muted' }, 'A short note. We will show this in rescue moments.'));
  host.appendChild(el('div', { class: 'field' },
    el('textarea', { rows: 3, value: draft.motivation ?? '', placeholder: 'time, calm, sleep, money, pride…',
      oninput: (e: Event) => draft.motivation = (e.target as HTMLTextAreaElement).value })
  ));
  host.appendChild(actions(ctx, { primary: 'Continue' }));
};

const stepTone: Step = (host, ctx) => {
  host.appendChild(el('h1', {}, 'How should the app speak to you?'));
  host.appendChild(choiceList(ctx, draft.tone, [
    { value: 'warm', label: 'Warm and gentle' },
    { value: 'calm', label: 'Calm and coach-like' },
    { value: 'playful', label: 'Playful and motivating' },
    { value: 'direct', label: 'Direct and grounded' },
  ], (v) => draft.tone = v));
  host.appendChild(actions(ctx, { primary: 'Continue', primaryDisabled: !draft.tone }));
};

const stepLocalNote: Step = (host, ctx) => {
  host.appendChild(el('h1', {}, 'Your data stays here.'));
  host.appendChild(el('p', { class: 'lead text-muted' },
    'Wean stores your plan locally on this device. We do not receive your habit data. Export a JSON backup any time. If you clear browser storage or lose the device, you may need that backup to restore your plan.'));
  host.appendChild(actions(ctx, { primary: 'I understand' }));
};

const stepReview: Step = (host, ctx) => {
  host.appendChild(el('h1', {}, 'Your plan is ready'));
  const summary = summarize();
  host.appendChild(el('div', { class: 'card' },
    ...summary.map((line) => el('div', {}, line))
  ));
  const row = el('div', { class: 'wizard-actions' },
    el('button', { class: 'btn ghost', onclick: () => ctx.back() }, 'Back'),
    el('button', { class: 'btn primary', onclick: () => ctx.finish() }, 'Start plan'),
  );
  host.appendChild(row);
};

function summarize(): string[] {
  const lines: string[] = [];
  const cat = HABIT_OPTIONS.find((h) => h.value === draft.category)?.label ?? 'Habit';
  const domain = draft.category ? CATEGORY_TO_DOMAIN[draft.category] : 'other';
  lines.push(`Reducing: ${cat}.`);
  if (domain === 'nicotine' && draft.nicotineMethodKinds && draft.nicotineMethodKinds.length > 0) {
    const labels = draft.nicotineMethodKinds
      .map((k) => NICOTINE_DEFAULT_METHODS.find((s) => s.kind === k)?.label ?? k)
      .join(', ');
    lines.push(`Tracking together: ${labels}.`);
  } else {
    lines.push(`One use = one ${draft.unitSingular ?? 'use'}.`);
  }
  if (draft.planType === 'interval') {
    lines.push(`Plan: stretch the gap between uses. Starting target ~${draft.intervalStartMin} min.`);
  } else if (draft.planType === 'window') {
    lines.push(`Plan: delay first use until ${minutesToTime(draft.windowFirstUseMinutes ?? 0)}.`);
  } else if (draft.planType === 'quota') {
    lines.push(`Plan: ${draft.quotaPeriod ?? 'weekly'} cap of ${draft.quotaStart ?? draft.quotaBaseline} uses.`);
  }
  const slipLabel: Record<string, string> = { gentle: 'Gentle', balanced: 'Reflective', direct: 'Structured' };
  lines.push(`Pace: ${draft.pace ?? 'steady'}. Recovery style: ${slipLabel[draft.slipStyle ?? 'balanced'] ?? 'Reflective'}.`);
  if (draft.moneyEnabled && draft.moneyCostPerUse) lines.push(`Cost per use: $${draft.moneyCostPerUse.toFixed(2)}.`);
  if (draft.motivation) lines.push(`Reason: ${draft.motivation}`);
  lines.push('Your data stays on this device.');
  return lines;
}

function minutesToTime(m: number): string {
  const h = Math.floor(m / 60); const mm = m % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}
function timeToMinutes(s: string): number {
  const [h, m] = s.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

function buildPurchaseTemplateFromOnboarding(habit: HabitRecord): PurchaseTemplateRecord | null {
  const t = draft.moneyEstimationType ?? 'per_use';
  const currency = draft.moneyCurrency ?? 'USD';
  const now = nowIso();
  const base: PurchaseTemplateRecord = {
    id: uuid(), schemaVersion: 2, habitId: habit.id,
    label: habit.label,
    currency,
    savingsMode: 'period_spend',
    confidence: 'low',
    active: true,
    createdAt: now, updatedAt: now,
  };
  if (t === 'per_use') {
    if (!draft.moneyCostPerUse || draft.moneyCostPerUse <= 0) return null;
    return {
      ...base,
      defaultPrice: draft.moneyCostPerUse,
      packageQuantity: 1,
      packageUnitLabel: habit.unitSingular,
      savingsMode: 'unit_avoidance' satisfies SavingsMode,
      confidence: 'medium',
    };
  }
  if (t === 'per_pack') {
    if (!draft.moneyPurchaseCost || !draft.moneyUnitsPerPurchase || draft.moneyUnitsPerPurchase <= 0) return null;
    return {
      ...base,
      defaultPrice: draft.moneyPurchaseCost,
      packageQuantity: draft.moneyUnitsPerPurchase,
      packageUnitLabel: habit.unitSingular,
      savingsMode: 'unit_avoidance',
      confidence: 'high',
    };
  }
  if (t === 'weekly_spend' || t === 'monthly_spend') {
    if (!draft.moneyEstimatedSpend || draft.moneyEstimatedSpend <= 0) return null;
    return {
      ...base,
      baselineSpendPerPeriod: {
        amount: draft.moneyEstimatedSpend,
        period: t === 'weekly_spend' ? 'week' : 'month',
      },
      savingsMode: 'period_spend',
      confidence: 'low',
    };
  }
  return null;
}

async function commit(): Promise<void> {
  const habitId = uuid();
  const planId = uuid();
  const category: HabitCategory = draft.category ?? 'other';
  const domain: BehaviorDomain = CATEGORY_TO_DOMAIN[category] ?? 'other';
  const habit: HabitRecord = {
    id: habitId, schemaVersion: 2,
    label: HABIT_OPTIONS.find((h) => h.value === category)?.label ?? 'Habit',
    privateLabel: draft.privateAlias || undefined,
    category,
    domain,
    unitSingular: draft.unitSingular ?? 'use',
    unitPlural: draft.unitPlural ?? 'uses',
    active: true, createdAt: nowIso(), updatedAt: nowIso(),
  };
  await habits.put(habit);

  // Seed consumption methods. For nicotine domain, honor multi-select from
  // stepNicotineMethods. For other domains, fall back to a single default.
  if (domain === 'nicotine' && draft.nicotineMethodKinds && draft.nicotineMethodKinds.length > 0) {
    for (const kind of draft.nicotineMethodKinds) {
      const seed = NICOTINE_DEFAULT_METHODS.find((s) => s.kind === kind);
      if (!seed) continue;
      await consumptionMethods.put(buildMethodFromSeed(habit, 'nicotine', seed));
    }
    // Update habit label/units to reflect the broader nicotine domain when
    // multiple methods are tracked.
    if (draft.nicotineMethodKinds.length > 1) {
      habit.label = 'Nicotine';
      habit.unitSingular = 'nicotine session';
      habit.unitPlural = 'nicotine sessions';
      habit.updatedAt = nowIso();
      await habits.put(habit);
    }
  } else {
    await consumptionMethods.put(defaultMethodForLegacyHabit(habit));
  }

  const planType: PlanType = draft.planType ?? 'interval';
  let config: IntervalConfig | WindowConfig | QuotaConfig;
  let state: IntervalState | WindowState | QuotaState;
  if (planType === 'interval') {
    const startMin = draft.intervalStartMin ?? 45;
    const baselineMin = draft.intervalBaselineMin ?? 30;
    const cfg: IntervalConfig = {
      baselineIntervalMs: baselineMin * 60_000,
      startingIntervalMs: startMin * 60_000,
      minIntervalMs: 5 * 60_000,
      maxIntervalMs: 12 * 60 * 60_000,
    };
    config = cfg;
    state = {
      planType: 'interval',
      targetIntervalMs: cfg.startingIntervalMs,
      baselineIntervalMs: cfg.baselineIntervalMs,
      minIntervalMs: cfg.minIntervalMs,
      maxIntervalMs: cfg.maxIntervalMs,
      successStreak: 0, slipStreak: 0, momentum: 50,
    };
  } else if (planType === 'window') {
    const firstUse = draft.windowFirstUseMinutes ?? (18 * 60);
    const cfg: WindowConfig = {
      windowName: draft.windowName ?? 'Daily',
      activeDays: [0, 1, 2, 3, 4, 5, 6],
      baselineFirstUseMinutes: Math.max(0, firstUse - 30),
      startingFirstUseMinutes: firstUse,
      shiftStepMinutes: 15,
    };
    config = cfg;
    state = {
      planType: 'window',
      windowName: cfg.windowName, activeDays: cfg.activeDays,
      baselineFirstUseMinutes: cfg.baselineFirstUseMinutes,
      targetFirstUseMinutes: cfg.startingFirstUseMinutes,
      shiftStepMinutes: cfg.shiftStepMinutes,
      successDays: 0, missDays: 0,
    };
  } else {
    const period = draft.quotaPeriod ?? 'weekly';
    const baseline = draft.quotaBaseline ?? 14;
    const start = draft.quotaStart ?? Math.max(1, Math.round(baseline * 0.85));
    const cfg: QuotaConfig = { period, baselineQuota: baseline, startingQuota: start, reductionStep: 1 };
    config = cfg;
    const periodStart = new Date();
    const ms = period === 'daily' ? 86400000 : period === 'weekly' ? 7 * 86400000 : 30 * 86400000;
    state = {
      planType: 'quota', period, baselineQuota: baseline, currentQuota: start,
      periodStartedAt: periodStart.toISOString(),
      periodEndsAt: new Date(periodStart.getTime() + ms).toISOString(),
      usedThisPeriod: 0, reductionStep: 1, successPeriods: 0,
    };
  }

  const plan: PlanRecord = {
    id: planId, habitId, schemaVersion: 1, planType,
    pace: draft.pace ?? 'steady', slipStyle: draft.slipStyle ?? 'balanced',
    goalStyle: draft.goal ?? [], config, active: true, createdAt: nowIso(), updatedAt: nowIso(),
  };
  await plans.put(plan);

  const ps: PlanStateRecord = { planId, habitId, schemaVersion: 1, state, updatedAt: nowIso() };
  await planStates.put(ps);

  if (draft.moneyEnabled) {
    const t = draft.moneyEstimationType ?? 'per_use';
    await moneyModels.put({
      habitId,
      enabled: true,
      currency: draft.moneyCurrency ?? 'USD',
      estimationType: t,
      costPerUse: t === 'per_use' ? draft.moneyCostPerUse : (derivedCostPerUse() ?? undefined),
      purchaseCost: t === 'per_pack' ? draft.moneyPurchaseCost : undefined,
      unitsPerPurchase: t === 'per_pack' ? draft.moneyUnitsPerPurchase : undefined,
      estimatedSpendPerPeriod: (t === 'weekly_spend' || t === 'monthly_spend') ? draft.moneyEstimatedSpend : undefined,
      period: t === 'weekly_spend' ? 'week' : t === 'monthly_spend' ? 'month' : undefined,
      labelAsEstimate: true, updatedAt: nowIso(),
    });

    // Seed a Phase 2 PurchaseTemplate so the new savings algorithms have a baseline.
    const tpl = buildPurchaseTemplateFromOnboarding(habit);
    if (tpl) await purchaseTemplates.put(tpl);
  }

  const cur = await settingsStore.get();
  const next: UserSettings = {
    ...cur,
    tone: draft.tone ?? cur.tone,
    preferredRescueTypes: draft.rescueTypes ?? cur.preferredRescueTypes,
    defaultRescueDurationSec: draft.rescueDurationSec ?? cur.defaultRescueDurationSec,
    motivationAnchor: draft.motivation,
    triggers: draft.triggers,
  };
  await settingsStore.set(next);

  // Only seed default-active pointer when there is no current default. Adding
  // a second plan should not silently swap the user's default habit.
  const existingDefault = await metaGet('active_habit_id');
  if (!existingDefault) {
    await metaSet('active_habit_id', habitId);
    await metaSet('active_plan_id', planId);
  }
  await metaSet('onboarding_complete', true);

  await logEvent({
    type: 'onboarding_completed', habitId, planId,
    occurredAt: nowIso(), localDate: localDate(), timezoneOffsetMinutes: tzOffsetMinutes(),
    payload: { planType },
  });
}
